// The option the "Always on Top" checkbox in the field properties sidebar writes.
export const ALWAYS_ON_TOP_SETTING_KEY = 'alwaysOnTop';

// Fields overlap each other on the form canvas, and they are stacked by
// `options.zIndex` — a counter the constructor bumps every time a field is added
// or moved, so the field touched last is the one drawn on top. That makes the
// stacking a side effect of the editing order rather than something a form
// creator can pin down, which is what "Always on Top" is for: it lifts a field
// into a band above every value that counter hands out. A field in the band
// keeps its own order inside it, so two of them still stack against each other
// the same way, but neither can ever fall behind a normal field.
//
// The band deliberately stops short of 99999: the sticky page header, the modal
// backdrop and the datepicker popups all sit at or above that, and a form field
// painting over the header would be a worse bug than the one this fixes. Both
// bands are clamped so neither can climb out of its own — the counter reaches
// the hundreds on the busiest form in the database, so the ceilings are only a
// backstop against a form edited far longer than any we have seen.
export const DEFAULT_Z_INDEX = 10;
export const NORMAL_Z_INDEX_CEILING = 89999;
export const ALWAYS_ON_TOP_Z_INDEX_BASE = 90000;
export const ALWAYS_ON_TOP_Z_INDEX_CEILING = 99998;

export const isAlwaysOnTop = (element) => Boolean(element?.options?.[ALWAYS_ON_TOP_SETTING_KEY]);

// A field saved before the counter existed, or one whose option came back as a
// string, still has to land somewhere sensible in its band.
const getStackingOrder = (element) => {
  const zIndex = Number(element?.options?.zIndex);

  return Number.isFinite(zIndex) && zIndex > 0 ? zIndex : DEFAULT_Z_INDEX;
};

const getAlwaysOnTopZIndex = (element) => Math.min(ALWAYS_ON_TOP_Z_INDEX_BASE + getStackingOrder(element), ALWAYS_ON_TOP_Z_INDEX_CEILING);

// The constructor canvas draws every field at its own stacking order, so the
// setting only has to move the field into the band above them.
export const getBuilderZIndex = (element) =>
  isAlwaysOnTop(element) ? getAlwaysOnTopZIndex(element) : Math.min(getStackingOrder(element), NORMAL_Z_INDEX_CEILING);

// The rendered form — preview, filling, the submission view and print — leaves
// its fields on `auto` and lets the document order decide, which is what puts a
// field behind the one that comes after it. Only an "Always on Top" field is
// given a z-index of its own, and that alone lifts it above every `auto`
// sibling, whichever order they are in.
export const getRenderedZIndex = (element) => (isAlwaysOnTop(element) ? getAlwaysOnTopZIndex(element) : 'auto');

// A field added to the canvas from now on starts pinned: it is placed on top of
// whatever it overlaps, and being drawn underneath a field it was dropped over
// is nearly always a surprise rather than the intent. The form creator unchecks
// it for the rare field meant to sit below another.
//
// Text is the exception. It is the layer a form is written on — headings, the
// paragraphs inputs are laid over — so pinning it would hide the very fields it
// labels. Fields already saved keep whatever they have; this default is only
// seeded when a field is created, and nothing rewrites an existing one.
export const ALWAYS_ON_TOP_DEFAULT_EXEMPT_TYPES = ['text'];

export const getDefaultAlwaysOnTop = (type) => !ALWAYS_ON_TOP_DEFAULT_EXEMPT_TYPES.includes(type);
